// src/hooks/use-onboarding-variant.ts
import { useEffect, useState } from "react";

import {
  normalizeVariant, onboardingRedirectTarget, ONBOARDING_A_PATH,
  ONBOARDING_VARIANT_FLAG, ONBOARDING_VARIANT_STORAGE_KEY, type OnboardingVariant,
} from "@/lib/onboarding-b-variant";
import { posthog } from "@/lib/posthog";

function readFrozen(): OnboardingVariant | null {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(ONBOARDING_VARIANT_STORAGE_KEY);
  return v === "control" || v === "educativo" ? v : null;
}

/**
 * Resolve a variante do A/B do onboarding.
 * - `enabled` true só para o público-alvo (professor global, não concluído).
 * - Espera `onFeatureFlags` (evita race: flag undefined no 1º render → flicker).
 * - Congela em localStorage: a variante NUNCA troca no meio do fluxo.
 * - Pré-requisito: identify do PostHog já rodou (onboarding é pós-login),
 *   então o bucketing por distinct_id é estável.
 * Retorna `null` enquanto não resolveu → o chamador NÃO deve redirecionar.
 */
export function useOnboardingVariant(enabled: boolean): OnboardingVariant | null {
  const [variant, setVariant] = useState<OnboardingVariant | null>(() => readFrozen());

  useEffect(() => {
    if (!enabled || variant !== null) return;
    let done = false;
    posthog.onFeatureFlags(() => {
      if (done) return;
      done = true;
      // getFeatureFlag dispara $feature_flag_called (exposição usada na análise A/B).
      const resolved = normalizeVariant(posthog.getFeatureFlag(ONBOARDING_VARIANT_FLAG));
      try { localStorage.setItem(ONBOARDING_VARIANT_STORAGE_KEY, resolved); } catch { /* noop */ }
      setVariant(resolved);
    });
  }, [enabled, variant]);

  return variant;
}

/**
 * Versão imperativa (para callbacks async, ex.: redirect pós-signup no auth-page,
 * onde não dá pra usar o hook). Resolve a variante uma vez, congela no
 * localStorage (mesma chave do hook → sem flicker e consistente com OnboardingGate)
 * e devolve o caminho do onboarding (A ou B). Espera os feature flags do PostHog
 * (até `timeoutMs`); no timeout cai em `control` (A) como fallback seguro.
 */
export function resolveOnboardingRedirectTarget(timeoutMs = 2500): Promise<string> {
  const frozen = readFrozen();
  if (frozen) {
    return Promise.resolve(onboardingRedirectTarget(frozen) ?? ONBOARDING_A_PATH);
  }
  return new Promise<string>((resolve) => {
    let done = false;
    const finish = (v: OnboardingVariant) => {
      if (done) return;
      done = true;
      try { localStorage.setItem(ONBOARDING_VARIANT_STORAGE_KEY, v); } catch { /* noop */ }
      resolve(onboardingRedirectTarget(v) ?? ONBOARDING_A_PATH);
    };
    try {
      posthog.onFeatureFlags(() => finish(normalizeVariant(posthog.getFeatureFlag(ONBOARDING_VARIANT_FLAG))));
    } catch {
      finish("control");
    }
    setTimeout(() => finish("control"), timeoutMs);
  });
}
